package br.com.vvrefeicoes.portal.data.repository

import br.com.vvrefeicoes.portal.domain.model.SocialConnection
import br.com.vvrefeicoes.portal.domain.model.SocialConnectionType
import br.com.vvrefeicoes.portal.domain.repository.AuthRepository
import br.com.vvrefeicoes.portal.domain.repository.SocialConnectionRepository
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.delete
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.client.statement.HttpResponse
import io.ktor.http.ContentType
import io.ktor.http.HttpHeaders
import io.ktor.http.contentType
import kotlinx.serialization.Serializable

class RemoteSocialConnectionRepository(
    private val httpClient: HttpClient,
    private val baseUrl: String,
    private val authRepository: AuthRepository,
) : SocialConnectionRepository {
    override suspend fun list(): List<SocialConnection> =
        request { httpClient.get("$baseUrl/social-connections") { authorize() } }
            .body<List<SocialConnectionDto>>()
            .map(SocialConnectionDto::toDomain)

    override suspend fun create(
        name: String,
        type: SocialConnectionType,
        credentials: Map<String, String>,
    ): SocialConnection =
        request {
            httpClient.post("$baseUrl/social-connections") {
                contentType(ContentType.Application.Json)
                setBody(CreateSocialConnectionDto(name, type.name, credentials))
                authorize()
            }
        }.body<SocialConnectionDto>().toDomain()

    override suspend fun get(id: String): SocialConnection =
        request { httpClient.get("$baseUrl/social-connections/$id") { authorize() } }
            .body<SocialConnectionDto>()
            .toDomain()

    override suspend fun connect(id: String): SocialConnection =
        request { httpClient.post("$baseUrl/social-connections/$id/connect") { authorize() } }
            .body<SocialConnectionDto>()
            .toDomain()

    override suspend fun delete(id: String) {
        request { httpClient.delete("$baseUrl/social-connections/$id") { authorize() } }
    }

    private suspend inline fun request(crossinline call: suspend () -> HttpResponse): HttpResponse {
        val response = call()
        if (response.status.value !in 200..299) error("Conexões sociais: HTTP ${response.status.value}")
        return response
    }

    private fun io.ktor.client.request.HttpRequestBuilder.authorize() {
        authRepository.session.value
            ?.token
            ?.let { header(HttpHeaders.Authorization, "Bearer $it") }
    }
}

@Serializable
private data class CreateSocialConnectionDto(
    val nome: String,
    val tipo: String,
    val credenciais: Map<String, String>,
)

@Serializable
private data class SocialConnectionDto(
    val id: String,
    val nome: String = "",
    val tipo: String = "BAILEYS",
    val status: String = "DISCONNECTED",
    val qrCode: String? = null,
    val phoneNumber: String? = null,
    val credentialsConfigured: Boolean = false,
    val lastConnectedAt: String? = null,
) {
    fun toDomain() =
        SocialConnection(
            id = id,
            name = nome,
            type = SocialConnectionType.entries.firstOrNull { it.name == tipo } ?: SocialConnectionType.BAILEYS,
            status = status,
            qrCode = qrCode,
            phoneNumber = phoneNumber,
            credentialsConfigured = credentialsConfigured,
            lastConnectedAt = lastConnectedAt,
        )
}
